import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SITE_URL } from "@/lib/site-config";

interface InternalLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  showArrow?: boolean;
}

export const InternalLink = ({ to, children, className = "", showArrow = false }: InternalLinkProps) => {
  return (
    <Link 
      to={to} 
      className={`text-primary hover:text-primary-dark underline transition-colors ${className}`}
      title={typeof children === 'string' ? children : undefined}
    >
      {children}
      {showArrow && <ArrowRight className="inline-block w-4 h-4 ml-1" />}
    </Link>
  );
};

// Strategic internal linking component for SEO
export const RelatedLinks = ({ 
  category, 
  current 
}: { 
  category: 'products' | 'services' | 'quality' | 'company'; 
  current?: string;
}) => {
  const linkSets = {
    products: [
      { to: '/products/human-apis', label: 'Human Pharmaceutical APIs', keywords: 'pharmaceutical APIs, human medicine APIs' },
      { to: '/products/intermediates', label: 'Pharmaceutical Intermediates', keywords: 'drug intermediates, synthesis building blocks' },
      { to: '/products/veterinary', label: 'Veterinary APIs', keywords: 'veterinary pharmaceuticals, animal health APIs' },
      { to: '/products', label: 'Complete Product Catalog', keywords: 'API catalog, pharmaceutical products' }
    ],
    services: [
      { to: '/capabilities', label: 'Manufacturing Capabilities', keywords: 'CMO services, CDMO pharmaceutical' },
      { to: '/quality', label: 'Quality Assurance & Compliance', keywords: 'ISO certification, GMP compliance, FDA' },
      { to: '/contact', label: 'Request Custom Quote', keywords: 'pharmaceutical quote, API pricing' }
    ],
    quality: [
      { to: '/quality', label: 'Quality Standards & Certifications', keywords: 'ISO 9001, GMP, regulatory compliance' },
      { to: '/capabilities', label: 'Testing & Analysis Capabilities', keywords: 'pharmaceutical testing, quality control' },
      { to: '/about', label: 'Company Accreditations', keywords: 'certifications, ISO certified manufacturer' }
    ],
    company: [
      { to: '/about', label: 'About Longshine Boi-Tech', keywords: 'pharmaceutical manufacturer, company history' },
      { to: '/news', label: 'Industry News & Updates', keywords: 'pharmaceutical news, regulatory updates' },
      { to: '/contact', label: 'Global Contact Information', keywords: 'pharmaceutical supplier contact, get quote' }
    ]
  };

  const links = linkSets[category].filter(link => link.to !== current);

  if (links.length === 0) return null;

  return (
    <nav className="mt-8 p-6 bg-muted/30 rounded-lg border border-border" aria-label="Related pages">
      <h3 className="text-lg font-semibold text-foreground mb-4">Related Pages</h3>
      <ul className="space-y-3">
        {links.map((link, index) => (
          <li key={index}>
            <Link 
              to={link.to}
              className="flex items-center text-primary hover:text-primary-dark transition-colors group"
              title={link.keywords}
            >
              <ArrowRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
              <span className="font-medium">{link.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// Breadcrumb component for hierarchical navigation and SEO
export const Breadcrumbs = ({ 
  items 
}: { 
  items: Array<{ label: string; to?: string }> 
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      ...(item.to && { "item": `${SITE_URL}${item.to}` })
    }))
  };

  return (
    <>
      <nav aria-label="Breadcrumb" className="mb-6">
        <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
          {items.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && <span className="mx-2">/</span>}
              {item.to ? (
                <Link 
                  to={item.to} 
                  className="hover:text-primary transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-foreground font-medium">{item.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </>
  );
};

// Footer navigation with SEO-optimized anchor text.
//
// Why this matters: footer links are sitewide — every page on the site
// passes link equity to every URL listed here. Adding all 8 product
// categories (was 3) and 6 therapeutic-area pages (was 0) takes the
// previously-orphaned long-tail landing pages and gives them ~500
// internal links each, which is what unsticks them in Google's crawl
// scheduler and lets them start ranking.
export const FooterLinks = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
      <div>
        <h4 className="font-semibold text-foreground mb-4">Pharmaceutical Products</h4>
        <ul className="space-y-2 text-sm">
          <li>
            <Link to="/products/human-apis" className="text-muted-foreground hover:text-primary transition-colors">
              Human Pharmaceutical APIs
            </Link>
          </li>
          <li>
            <Link to="/products/intermediates" className="text-muted-foreground hover:text-primary transition-colors">
              Drug Intermediates
            </Link>
          </li>
          <li>
            <Link to="/products/veterinary" className="text-muted-foreground hover:text-primary transition-colors">
              Veterinary APIs
            </Link>
          </li>
          <li>
            <Link to="/products/peptides" className="text-muted-foreground hover:text-primary transition-colors">
              Peptides & Custom Synthesis
            </Link>
          </li>
          <li>
            <Link to="/products/excipients" className="text-muted-foreground hover:text-primary transition-colors">
              Pharmaceutical Excipients
            </Link>
          </li>
          <li>
            <Link to="/products/amino-acids" className="text-muted-foreground hover:text-primary transition-colors">
              Amino Acids
            </Link>
          </li>
          <li>
            <Link to="/products/nootropics" className="text-muted-foreground hover:text-primary transition-colors">
              Nootropics
            </Link>
          </li>
          <li>
            <Link to="/products/research-chemicals" className="text-muted-foreground hover:text-primary transition-colors">
              Research Chemicals
            </Link>
          </li>
          <li>
            <Link to="/products" className="text-muted-foreground hover:text-primary transition-colors font-medium">
              Complete catalogue →
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-foreground mb-4">Therapeutic Areas</h4>
        <ul className="space-y-2 text-sm">
          <li>
            <Link to="/therapeutic/antiviral" className="text-muted-foreground hover:text-primary transition-colors">
              Antiviral APIs
            </Link>
          </li>
          <li>
            <Link to="/therapeutic/antibiotic" className="text-muted-foreground hover:text-primary transition-colors">
              Antibiotic APIs
            </Link>
          </li>
          <li>
            <Link to="/therapeutic/cardiovascular" className="text-muted-foreground hover:text-primary transition-colors">
              Cardiovascular APIs
            </Link>
          </li>
          <li>
            <Link to="/therapeutic/diabetes" className="text-muted-foreground hover:text-primary transition-colors">
              Diabetes & Endocrine
            </Link>
          </li>
          <li>
            <Link to="/therapeutic/oncology" className="text-muted-foreground hover:text-primary transition-colors">
              Oncology APIs
            </Link>
          </li>
          <li>
            <Link to="/therapeutic/cns" className="text-muted-foreground hover:text-primary transition-colors">
              CNS / Neurology APIs
            </Link>
          </li>
          <li>
            <Link to="/capabilities" className="text-muted-foreground hover:text-primary transition-colors">
              CDMO Manufacturing
            </Link>
          </li>
          <li>
            <Link to="/quality" className="text-muted-foreground hover:text-primary transition-colors">
              Quality & Regulatory
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-foreground mb-4">Markets & Compliance</h4>
        <ul className="space-y-2 text-sm">
          <li>
            <Link to="/certifications" className="text-muted-foreground hover:text-primary transition-colors">
              GMP / FDA DMF / CEP Certifications
            </Link>
          </li>
          <li>
            <Link to="/markets/north-america" className="text-muted-foreground hover:text-primary transition-colors">
              APIs for North America (US · CA · MX)
            </Link>
          </li>
          <li>
            <Link to="/markets/europe" className="text-muted-foreground hover:text-primary transition-colors">
              APIs for Europe (EU · UK · CH)
            </Link>
          </li>
          <li>
            <Link to="/markets/latin-america" className="text-muted-foreground hover:text-primary transition-colors">
              APIs for Latin America
            </Link>
          </li>
          <li>
            <Link to="/markets/asia-pacific" className="text-muted-foreground hover:text-primary transition-colors">
              APIs for Asia-Pacific
            </Link>
          </li>
          <li>
            <Link to="/markets/middle-east-africa" className="text-muted-foreground hover:text-primary transition-colors">
              APIs for Middle East & Africa
            </Link>
          </li>
          <li>
            <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
              About Longshine Boi-Tech
            </Link>
          </li>
          <li>
            <Link to="/news" className="text-muted-foreground hover:text-primary transition-colors">
              Pharmaceutical Industry News
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-foreground mb-4">Customer Support</h4>
        <ul className="space-y-2 text-sm">
          <li>
            <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
              Request Pharmaceutical API Quote
            </Link>
          </li>
          <li>
            <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
              Technical Support & Documentation
            </Link>
          </li>
          <li>
            <Link to="/products" className="text-muted-foreground hover:text-primary transition-colors">
              Download Product Specifications & COA
            </Link>
          </li>
          <li>
            <Link to="/quality" className="text-muted-foreground hover:text-primary transition-colors">
              Regulatory Compliance Documents
            </Link>
          </li>
          <li>
            <Link to="/return-policy" className="text-muted-foreground hover:text-primary transition-colors">
              Return & Refund Policy
            </Link>
          </li>
          <li>
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};
